import { getSnowdonJwt } from '~/domains/auth/utils';
import type { OAIError, OAIPathKeysByMethod } from '~/types/openapi';
import { logError } from '~/utils/datadog';
import { isClient, isServer } from '~/utils/ssr';
import { INTERNAL_MARKET_API_URL, MARKET_API_URL } from '~/utils/url';

import { OAIApiError } from './openApiError';

export const getBaseUrl = (): string => {
  if (isServer()) {
    return INTERNAL_MARKET_API_URL;
  }

  return MARKET_API_URL;
};

export const getRequestUrl = (
  queryKey: string,
  variables?: Record<string, unknown>,
) => {
  let url = `${getBaseUrl()}${queryKey}`;

  const paramKeys = (queryKey.match(/{[a-zA-z-]+}/g) ?? []).map((param) =>
    param.replace(/[{}]/g, ''),
  );
  paramKeys.forEach((param) => {
    url = url.replace(`{${param}}`, variables?.[param] as string);
  });

  const qs = new URLSearchParams(
    Object.entries(variables ?? {}).reduce((current, [key, value]) => {
      if (paramKeys.includes(key)) {
        return current;
      }

      return {
        ...current,
        [key]: value,
      };
    }, {}),
  ).toString();

  if (qs) {
    url = `${url}?${qs}`;
  }

  return url;
};

export const getRequestBody = ({
  requestBody,
  formData,
}: {
  requestBody?: Record<string, unknown>;
  formData?: Record<string, string | Blob>;
}) => {
  let body;
  if (requestBody) {
    body = JSON.stringify(requestBody);
  }

  if (formData) {
    const form = new FormData();
    Object.keys(formData).forEach((key) => {
      form.append(key, formData[key]);
    });
    body = form;
  }

  return body;
};

export function getOAIErrorMessage(err: OAIApiError) {
  return err.userMessage || err.message;
}

export const fetchOAIData = async <TData>({
  queryKey,
  variables,
  fetchOptions,
}: {
  queryKey: OAIPathKeysByMethod<'get'>;
  variables?: Record<string, unknown>;
  fetchOptions?: RequestInit;
}) => {
  const fetchOption = {
    ...fetchOptions,
    headers: {
      ...fetchOptions?.headers,
      'X-Market-Front-Page': isClient() ? window.location.href : 'server',
      ...(isClient()
        ? {
            'X-Client-Auth-Token': getSnowdonJwt(),
          }
        : {}),
    },
    credentials: 'include' as RequestCredentials,
  };

  const response = await fetch(getRequestUrl(queryKey, variables), fetchOption);

  const data = (
    response.headers.get('content-type')?.includes('application/json')
      ? await response.json()
      : await response.text()
  ) as unknown;

  if (!response.ok) {
    const error = new OAIApiError(
      response.status,
      data as OAIError,
      response.url,
    );
    // 451(성인전용/19금)은 기대된 비즈니스 응답이므로 Datadog 에러 로깅 제외 (MKPC-3616)
    if (error.statusCode !== 451) {
      logError(error);
    }

    throw error;
  }

  return data as TData;
};
